import { TOOLTIP_PORTAL_ID } from '@/consts/portal.consts';
import { TooltipOptions } from '@components/ui/Tooltip/types';
import {
  FloatingPortal,
  useDelayGroup,
  useMergeRefs,
  useTransitionStyles
} from '@floating-ui/react';
import {
  cloneElement,
  forwardRef,
  HTMLProps,
  isValidElement,
  PropsWithChildren
} from 'react';
import { TooltipContext, useTooltipContext } from './TooltipContext';
import { useTooltip } from './useTooltip';

type TooltipProps = PropsWithChildren<TooltipOptions>;

export const Tooltip = ({ children, ...options }: TooltipProps) => {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const tooltip = useTooltip(options);
  return (
    <TooltipContext.Provider value={tooltip}>
      {children}
    </TooltipContext.Provider>
  );
};

export const TooltipTrigger = forwardRef<
  HTMLElement,
  HTMLProps<HTMLElement> & { asChild?: boolean }
>(({ children, asChild = false, ...props }, propRef) => {
  const state = useTooltipContext();

  const childrenRef = (children as any).ref;
  const ref = useMergeRefs([state.refs.setReference, propRef, childrenRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && isValidElement(children)) {
    return cloneElement(
      children,
      state.getReferenceProps({
        ref,
        ...props,
        ...(children.props as any),
        'data-state': state.open ? 'open' : 'closed'
      })
    );
  }

  return (
    <div
      ref={ref}
      // The user can style the trigger based on the state
      data-state={state.open ? 'open' : 'closed'}
      {...state.getReferenceProps(props)}
    >
      {children}
    </div>
  );
});

export const TooltipContent = forwardRef<
  HTMLDivElement,
  HTMLProps<HTMLDivElement>
>((props, propRef) => {
  const tooltipContext = useTooltipContext();
  const { isInstantPhase, currentId } = useDelayGroup(tooltipContext as any);
  const ref = useMergeRefs([tooltipContext.refs.setFloating, propRef]);

  useDelayGroup(tooltipContext.context, {
    id: tooltipContext.context.floatingId
  });

  const instantDuration = 0;
  const duration = 250;

  const { isMounted, styles } = useTransitionStyles(tooltipContext.context, {
    duration: isInstantPhase
      ? {
          open: instantDuration,
          // `id` is this component's `id`
          // `currentId` is the current group's `id`
          close:
            currentId === tooltipContext.context.floatingId
              ? duration
              : instantDuration
        }
      : duration,
    initial: {
      opacity: 0
    }
  });

  if (!isMounted) return null;

  return (
    <FloatingPortal root={document.getElementById(TOOLTIP_PORTAL_ID)}>
      <div
        ref={ref}
        style={{
          ...tooltipContext.floatingStyles,
          ...props.style,
          ...styles
        }}
        {...tooltipContext.getFloatingProps(props)}
      />
    </FloatingPortal>
  );
});
